import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState, type UIEvent } from 'react';

import { useDebouncedCallback } from './use-debounced-callback';

/** One page of a cursor-paginated dropdown source. `nextCursor` is present only when
 *  `hasNextPage` is true; it is opaque and echoed straight back to `fetchPage`. */
export type CursorPage<TItem> = {
  data: TItem[];
  nextCursor?: string;
  hasNextPage: boolean;
};

export type SelectOption = { value: string; label: string };

export type UseCursorSelectOptions<TItem> = {
  /** React Query key for the current search term. Must vary by `search` so a new term
   *  starts a fresh query (which resets the cursor to the first page). */
  queryKey: (search: string) => readonly unknown[];
  /** Fetches one page. `cursor` is undefined for the first page of a term. */
  fetchPage: (params: {
    search: string;
    cursor?: string;
    limit: number;
  }) => Promise<CursorPage<TItem>>;
  toOption: (item: TItem) => SelectOption;
  /** Page size; small by default so the first open is fast and scrolling fetches more. */
  limit?: number;
  enabled?: boolean;
  debounceMs?: number;
  /** Pixels from the bottom of the popup at which the next page is fetched. */
  scrollThreshold?: number;
};

/** Drives an antd `<Select>` backed by a cursor-paginated, server-searched source:
 *  debounced search, infinite scroll (fetch-next-on-popup-scroll), and flattened options.
 *  Spread the returned `showSearch`/`options`/`loading`/`onPopupScroll` onto the Select.
 *
 *  Changing the search term swaps the query key, so the cursor resets to the first page —
 *  a cursor is only valid for the term it was issued under. */
export function useCursorSelect<TItem>({
  queryKey,
  fetchPage,
  toOption,
  limit = 20,
  enabled = true,
  debounceMs = 300,
  scrollThreshold = 32,
}: UseCursorSelectOptions<TItem>) {
  const [search, setSearch] = useState('');
  const onSearch = useDebouncedCallback((term: string) => setSearch(term), debounceMs);

  // Clear the term immediately (cancelling any pending debounced set). antd clears its own
  // search input on close, but our `search` state would otherwise persist — a persistent
  // (non-remounting) consumer must call this on dropdown close so reopening isn't still filtered.
  const resetSearch = () => {
    onSearch.cancel();
    setSearch('');
  };

  // The caller owns the key and encodes the only cache-busting input (`search`). `fetchPage`
  // and `limit` are behaviourally constant; keying on `fetchPage` would refetch every render
  // because callers pass an inline closure.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useInfiniteQuery({
    queryKey: queryKey(search),
    queryFn: ({ pageParam }) =>
      fetchPage({ search, cursor: pageParam as string | undefined, limit }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasNextPage ? last.nextCursor : undefined),
    placeholderData: keepPreviousData,
    enabled,
  });

  const options = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.data).map(toOption),
    [query.data, toOption],
  );

  const onPopupScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - scrollThreshold;
    if (nearBottom && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  };

  return {
    /** Flattened, mapped options across every loaded page. */
    options,
    /** Wire to the Select: `showSearch={selectProps.showSearch}` etc. */
    showSearch: { onSearch, filterOption: false as const },
    /** Clear the search term immediately. Call on dropdown close for persistent (non-remounting)
     *  consumers so a stale term doesn't keep the reopened list filtered. */
    resetSearch,
    loading: query.isFetching,
    onPopupScroll,
    /** Escape hatch for callers needing raw query state. */
    query,
  };
}
